import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowRight, ArrowLeft, Truck, Sofa, Heart, Trees, Users, ShieldCheck, MapPin } from "lucide-react";
import Header from "@/components/Header";
import SiteFooter from "@/components/SiteFooter";
import PageHero from "@/components/PageHero";
import BookCTA from "@/components/BookCTA";
import { findArea } from "@/lib/areas";
import { COMPANY } from "@/lib/brand";
import { usePageMeta } from "@/lib/seo";

const servicesFor = (name) => [
  { icon: Truck, title: `Full house clearance in ${name}`, body: "Every room, loft, garage and shed cleared in one visit — ideal for moving, downsizing or end-of-tenancy." },
  { icon: Sofa, title: "Single item removal", body: "One sofa, fridge or mattress? We'll collect it at a time that suits you, with no job too small." },
  { icon: Heart, title: "Probate clearance", body: "A discreet, respectful clearance of a loved one's home, at your pace and around solicitors and executors." },
  { icon: Trees, title: "Garden clearance", body: "Green waste, old sheds, fence panels and garden furniture cleared and taken away responsibly." },
];

function AreaIntro({ area }) {
  const { name, county } = area;
  const isHome = area.slug === "peterborough";
  return (
    <div className="mt-6 max-w-2xl space-y-4 text-lg text-slate-600 leading-relaxed" data-testid="area-intro">
      <p>
        Waites Clearances provides friendly, reliable house clearance in {name}, {county}. We're a family run team{" "}
        {isHome ? "based right here in Peterborough" : "based in Peterborough, just a short drive away"}, and we're
        happy to help homeowners, landlords and families in {name} with everything from a single unwanted item to a
        complete property clearance.
      </p>
      <p>
        Whatever you need clearing in {name} — a full house, a few bulky items, a probate estate or an overgrown
        garden — we handle the heavy lifting, loading and responsible disposal, recycling and donating wherever we
        can. As Environment Agency registered waste carriers (Licence No. {COMPANY.wasteCarrierLicence}), you can
        trust that everything we collect in {name} is disposed of legally and properly.
      </p>
    </div>
  );
}

function AreaServices({ name }) {
  return (
    <section className="py-20 sm:py-24 bg-white border-y border-slate-100" data-testid="area-services">
      <div className="max-w-5xl mx-auto px-6 sm:px-10">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#FF7F00]">What we clear in {name}</p>
        <h2 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
          Clearances of every size across {name}.
        </h2>
        <div className="mt-10 grid sm:grid-cols-2 gap-5">
          {servicesFor(name).map(({ icon: Icon, title, body }, i) => (
            <div key={title} data-testid={`area-service-${i}`} className="rounded-2xl border border-slate-200 bg-[#FAF8F5] p-6 hover:border-[#FFB870] hover:shadow-lg hover:-translate-y-0.5 transition-all">
              <div className="h-11 w-11 rounded-xl bg-[#FFF3E6] border border-[#FFB870] flex items-center justify-center mb-4">
                <Icon className="h-5 w-5 text-[#FF7F00]" />
              </div>
              <h3 className="font-display text-lg font-bold text-slate-900">{title}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function AreaTrust() {
  const items = [
    { icon: Users, label: "Family run team" },
    { icon: ShieldCheck, label: `EA registered · ${COMPANY.wasteCarrierLicence}` },
    { icon: MapPin, label: "Based in Peterborough" },
  ];
  return (
    <div className="mt-8 flex flex-wrap gap-x-6 gap-y-3" data-testid="area-trust">
      {items.map(({ icon: Icon, label }) => (
        <span key={label} className="inline-flex items-center gap-2 text-sm text-slate-600">
          <Icon className="h-4 w-4 text-[#FF7F00]" /> {label}
        </span>
      ))}
    </div>
  );
}

export default function AreaPage() {
  const { slug } = useParams();
  const area = findArea(slug);
  const name = area?.name ?? "";
  usePageMeta(
    `House Clearance ${name} | Waites Clearances`,
    `House clearance ${name} — full house, single item, probate and garden clearances in ${name} by a family run, EA registered team based in Peterborough. Get a free quote.`
  );
  if (!area) return <Navigate to="/areas" replace />;

  return (
    <div className="min-h-screen bg-[#FAF8F5]" data-testid={`page-area-${area.slug}`}>
      <Header />
      <PageHero eyebrow={`Areas we cover · ${area.county}`} title={`House Clearance ${name}`}>
        <AreaIntro area={area} />
        <div className="mt-8 flex flex-wrap items-center gap-3">
          <Link
            to="/book"
            data-testid="area-cta-book"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-[#FF7F00] hover:bg-[#E67000] text-white font-bold shadow-md shadow-orange-200 active:scale-[0.98] transition-[background-color,transform]"
          >
            Get a Free Quote <ArrowRight className="h-4 w-4" />
          </Link>
          <Link to="/areas" data-testid="area-back-link" className="inline-flex items-center gap-2 h-12 px-5 text-slate-600 hover:text-[#FF7F00] font-semibold transition-colors">
            <ArrowLeft className="h-4 w-4" /> All areas
          </Link>
        </div>
        <AreaTrust />
      </PageHero>
      <AreaServices name={name} />
      <BookCTA
        title={`Need a clearance in ${name}?`}
        body={`Send us a few details and photos and we'll get back to you with a free, no-obligation quote for your ${name} clearance.`}
      />
      <SiteFooter />
    </div>
  );
}
